import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Send, Paperclip, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEmailConfig } from "@/hooks/useEmailConfig";
import RichTextEditor from "@/components/ui/rich-text-editor";

interface MailComposeProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  replyTo?: {
    email: string;
    subject: string;
    messageId?: string;
  } | null;
  onSent?: () => void;
}

export function MailCompose({
  open,
  onOpenChange,
  replyTo,
  onSent,
}: MailComposeProps) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  const { toast } = useToast();
  const { primaryConfig, loading: emailConfigLoading } = useEmailConfig();

  useEffect(() => {
    if (replyTo) {
      setTo(replyTo.email);
      setSubject(
        replyTo.subject.startsWith("Re: ")
          ? replyTo.subject
          : `Re: ${replyTo.subject}`
      );
    } else {
      setTo("");
      setSubject("");
    }
    setBody("");
    setAttachments([]);
  }, [replyTo, open]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!to.trim()) {
      toast({
        title: "Error",
        description: "Please enter a recipient email",
        variant: "destructive",
      });
      return;
    }

    if (!body.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message",
        variant: "destructive",
      });
      return;
    }

    if (!primaryConfig) {
      toast({
        title: "Error",
        description:
          "No email configuration found. Please set up your email in settings.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSending(true);

      const attachmentData = await Promise.all(
        attachments.map(async (file) => {
          const arrayBuffer = await file.arrayBuffer();
          const base64 = btoa(
            new Uint8Array(arrayBuffer).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ""
            )
          );
          return {
            filename: file.name,
            content: base64,
            content_type: file.type,
          };
        })
      );

      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          communicationId: crypto.randomUUID(),
          to: to.trim(),
          subject: subject || "(No Subject)",
          body: body,
          fromEmail: primaryConfig.email_address,
          fromName: primaryConfig.display_name,
          bccEmail: primaryConfig.bcc_to_personal,
          inReplyTo: replyTo?.messageId,
          attachments: attachmentData.length > 0 ? attachmentData : undefined,
        },
      });

      if (error) throw error;

      toast({
        title: "Email sent",
        description: `Email sent successfully to ${to}`,
      });

      onSent?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast({
        title: "Email failed",
        description: error.message || "Failed to send email",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{replyTo ? "Reply to Email" : "Compose Email"}</DialogTitle>
          <DialogDescription>
            {replyTo ? "Send a reply to this email" : "Compose and send a new email"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="to">To</Label>
            <Input
              id="to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject..."
            />
          </div>

          {primaryConfig && (
            <div className="text-sm text-muted-foreground bg-muted/30 p-2 rounded">
              From: {primaryConfig.display_name || primaryConfig.email_address}
              {primaryConfig.bcc_to_personal && (
                <span className="ml-2">(BCC: {primaryConfig.bcc_to_personal})</span>
              )}
            </div>
          )}

          {!primaryConfig && !emailConfigLoading && (
            <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
              No email configuration found. Please set up your email in Email
              Settings.
            </div>
          )}

          <div className="space-y-2">
            <Label>Message</Label>
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder="Type your message..."
              minHeight="200px"
            />
          </div>

          <div className="space-y-2">
            <Label>Attachments</Label>
            <div className="flex flex-wrap gap-2">
              {attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-md text-sm"
                >
                  <Paperclip className="h-3 w-3" />
                  <span className="truncate max-w-[150px]">{file.name}</span>
                  <button
                    onClick={() => removeAttachment(index)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="cursor-pointer">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-3 py-1.5 border border-dashed rounded-md text-sm text-muted-foreground hover:text-foreground hover:border-foreground transition-colors">
                  <Paperclip className="h-3 w-3" />
                  Add file
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !body.trim() || !to.trim() || !primaryConfig}
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-1.5" />
                Send Email
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
